import { format, parse } from 'date-fns';
import { FC, KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
	Box,
	Button,
	DateRange,
	DateRangePicker,
	Dropdown,
	DropdownInput,
	DropdownMenu,
	DropdownOption,
	Flex,
	HStack,
	Input,
	InputGroup,
	InputLeftElement,
	rem,
	SearchOutline,
	useMenuContext,
	VStack,
	XOutline,
} from '@bonusly/design-system';

import { useUserGroups } from '../../../../queries/groups';
import { useTimeframeOptions } from './hooks/use-timeframe';
import { LeaderboardState, ScopeType, TimeframeOption } from './types';

interface ScopeOption {
	value: string;
	label: string;
}

// Threshold above which a search input is shown to filter the options.
// Mirrors the pattern used by the bonus-feed AddFeedModal SelectionModalContent.
const SEARCH_INPUT_THRESHOLD = 10;

/**
 * Renders the scope (team / department) options as plain Chakra `<Button>`s with an inline
 * search input above the list when there are more than `SEARCH_INPUT_THRESHOLD` options.
 *
 * Mirrors the proven-fast pattern used by the Bonus Feed "Add feed" modal
 * (`AddFeedModal.tsx > SelectionModalContent`): plain buttons render much cheaper than
 * Chakra `MenuItem` (no `useMenuItem` descendant registration, no shared menu context fan-out),
 * so even tenants with thousands of teams open the dropdown without freezing the main thread.
 *
 * Combined with the parent `Dropdown`'s `isLazy`, the buttons aren't mounted until the user
 * opens the dropdown, and once mounted the search input lets the user filter to a handful of
 * matching rows in a few keystrokes.
 *
 * Selection closes the menu via `useMenuContext().onClose` (replicating Chakra `MenuItem`'s
 * default close-on-click behavior, which plain `<Button>` does not provide).
 */
const ScopeOptionsList: FC<{
	options: ScopeOption[];
	selectedValue: string | null;
	onSelect: (value: string) => void;
	searchPlaceholder: string;
}> = ({ options, selectedValue, onSelect, searchPlaceholder }) => {
	const [searchTerm, setSearchTerm] = useState('');
	const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
	const { onClose } = useMenuContext();

	// Reset the search term when switching between teams/departments so the new tab starts
	// from a clean state.
	useEffect(() => {
		setSearchTerm('');
	}, [options]);

	const filteredOptions = useMemo(() => {
		if (!searchTerm) return options;
		const needle = searchTerm.toLowerCase();
		return options.filter((option) => option.label.toLowerCase().includes(needle));
	}, [options, searchTerm]);

	const handleSelect = (value: string) => {
		onSelect(value);
		onClose();
	};

	const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
		if (e.key === 'ArrowDown') {
			e.preventDefault();
			const next = index < filteredOptions.length - 1 ? index + 1 : 0;
			buttonRefs.current[next]?.focus();
		} else if (e.key === 'ArrowUp') {
			e.preventDefault();
			const prev = index > 0 ? index - 1 : filteredOptions.length - 1;
			buttonRefs.current[prev]?.focus();
		}
	};

	const showSearch = options.length > SEARCH_INPUT_THRESHOLD;

	return (
		<Box flex={1} display="flex" flexDirection="column" minH={0}>
			{showSearch && (
				<Box px={rem(8)} pt={rem(8)} flexShrink={0}>
					<InputGroup size="sm">
						<InputLeftElement pointerEvents="none">
							<SearchOutline boxSize={rem(16)} color="text.softer" />
						</InputLeftElement>
						<Input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder={searchPlaceholder} borderRadius="md" />
					</InputGroup>
				</Box>
			)}
			<Box flex={1} overflowY="auto" minH={0}>
				{filteredOptions.map((option, index) => {
					const isSelected = option.value === selectedValue;
					return (
						<Button
							key={option.value}
							ref={(el: HTMLButtonElement | null) => {
								buttonRefs.current[index] = el;
							}}
							w="100%"
							justifyContent="flex-start"
							textAlign="left"
							borderRadius={0}
							px={rem(12)}
							h={rem(36)}
							minH={rem(36)}
							flexShrink={0}
							fontSize="md"
							fontWeight="normal"
							color="text.default"
							bg={isSelected ? 'primary.bgAlpha' : 'transparent'}
							_hover={{ bg: isSelected ? 'primary.bgAlpha' : 'secondary.bgAlpha' }}
							_focus={{ bg: isSelected ? 'primary.bgAlpha' : 'secondary.bgAlpha' }}
							_focusVisible={{
								outlineColor: 'border.focus',
								outlineStyle: 'solid',
								outlineWidth: '2px',
							}}
							onClick={() => handleSelect(option.value)}
							onKeyDown={(e) => handleKeyDown(e, index)}
							title={option.label}
						>
							<Box as="span" w="100%" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
								{option.label}
							</Box>
						</Button>
					);
				})}
			</Box>
		</Box>
	);
};

interface LeaderboardFiltersProps {
	state: LeaderboardState;
	onScopeChange: (scopeType: ScopeType, teamId: string | null, departmentId: string | null) => void;
	onTimeframeChange: (timeframe: TimeframeOption) => void;
	onCustomDateChange: (startDate: string, endDate: string) => void;
}

type ScopeTab = 'teams' | 'departments';

export const LeaderboardFilters: FC<LeaderboardFiltersProps> = ({ state, onScopeChange, onTimeframeChange, onCustomDateChange }) => {
	const { t } = useTranslation('navigation');
	const timeframeOptions = useTimeframeOptions();
	const { data: userGroups } = useUserGroups();

	// Track which tab is selected in the scope dropdown - sync with active filter
	const [scopeTab, setScopeTab] = useState<ScopeTab>(state.scopeType === 'department' ? 'departments' : 'teams');

	// Keep scopeTab in sync if scopeType changes externally (e.g., clearing filters)
	useEffect(() => {
		if (state.scopeType === 'department') {
			setScopeTab('departments');
		} else if (state.scopeType === 'team') {
			setScopeTab('teams');
		}
		// Don't change tab when scopeType is 'company' to preserve user's last tab selection
	}, [state.scopeType]);

	// Parse custom dates for DateRangePicker
	const customDateRange: DateRange = useMemo(() => {
		if (state.customStartDate && state.customEndDate) {
			return [parse(state.customStartDate, 'yyyy-MM-dd', new Date()), parse(state.customEndDate, 'yyyy-MM-dd', new Date())];
		}
		return [null, null];
	}, [state.customStartDate, state.customEndDate]);

	const handleCustomDateRangeChange = (range: DateRange) => {
		const [start, end] = range;
		if (start && end) {
			onCustomDateChange(format(start, 'yyyy-MM-dd'), format(end, 'yyyy-MM-dd'));
		}
	};

	// Build team and department options from user groups
	const teamOptions = useMemo(() => {
		if (!userGroups?.teams) return [];
		return userGroups.teams.map((team) => ({
			value: team.key,
			label: team.name,
		}));
	}, [userGroups?.teams]);

	const departmentOptions = useMemo(() => {
		if (!userGroups?.departments) return [];
		return userGroups.departments.map((dept) => ({
			value: dept.key,
			label: dept.name,
		}));
	}, [userGroups?.departments]);

	// Determine current scope display value
	const getScopeDisplayValue = () => {
		if (state.scopeType === 'team' && state.selectedTeam) {
			const team = teamOptions.find((t) => t.value === state.selectedTeam);
			return team?.label ?? state.selectedTeam;
		}
		if (state.scopeType === 'department' && state.selectedDepartment) {
			const dept = departmentOptions.find((d) => d.value === state.selectedDepartment);
			return dept?.label ?? state.selectedDepartment;
		}
		// Default to "Company" when no filter is applied
		return t('leaderboard.filters.company');
	};

	const handleScopeSelect = (value: string) => {
		if (scopeTab === 'teams') {
			onScopeChange('team', value, null);
		} else {
			onScopeChange('department', null, value);
		}
	};

	const handleClearScope = () => {
		onScopeChange('company', null, null);
	};

	const hasActiveScope = state.scopeType !== 'company';
	const currentOptions = scopeTab === 'teams' ? teamOptions : departmentOptions;
	const hasTeams = teamOptions.length > 0;
	const hasDepartments = departmentOptions.length > 0;
	const hasScopeOptions = hasTeams || hasDepartments;

	return (
		<VStack w="100%" spacing={rem(8)} align="stretch">
			<HStack spacing={rem(8)}>
				{/* Timeframe dropdown - fixed small width */}
				<Dropdown
					onChange={(value) => onTimeframeChange(value as TimeframeOption)}
					value={state.selectedTimeframe}
					renderOption={(value) => timeframeOptions.find((opt) => opt.value === value)?.label ?? ''}
					size="md"
				>
					<DropdownInput
						fontSize="md"
						fontWeight="medium"
						h={rem(40)}
						borderRadius={rem(12)}
						borderColor="border.default"
						px={rem(12)}
						w={rem(120)}
						minW={rem(120)}
						maxW={rem(120)}
					/>
					<DropdownMenu zIndex="tooltip">
						{timeframeOptions.map((option) => (
							<DropdownOption key={option.value} value={option.value}>
								{option.label}
							</DropdownOption>
						))}
					</DropdownMenu>
				</Dropdown>

				{/* Scope dropdown (Teams/Departments) - only show if there are options */}
				{hasScopeOptions && (
					<Box flex={1} minW={0}>
						<Dropdown
							isLazy
							onChange={handleScopeSelect}
							value={
								state.scopeType === 'team'
									? (state.selectedTeam ?? 'company')
									: state.scopeType === 'department'
										? (state.selectedDepartment ?? 'company')
										: 'company'
							}
							renderOption={() => getScopeDisplayValue()}
							size="md"
						>
							<HStack spacing={0}>
								<DropdownInput
									fontSize="md"
									fontWeight="medium"
									h={rem(40)}
									borderRadius={hasActiveScope ? `${rem(12)} 0 0 ${rem(12)}` : rem(12)}
									borderColor="border.default"
									px={rem(14)}
									flex={1}
									minW={0}
									overflow="hidden"
									textOverflow="ellipsis"
									whiteSpace="nowrap"
								/>
								{hasActiveScope && (
									<Flex
										as="button"
										alignItems="center"
										justifyContent="center"
										h={rem(40)}
										px={rem(10)}
										borderRadius={`0 ${rem(12)} ${rem(12)} 0`}
										border="1px solid"
										borderColor="border.default"
										borderLeft="none"
										bg="primary.bgAlpha"
										cursor="pointer"
										flexShrink={0}
										onClick={(e) => {
											e.stopPropagation();
											handleClearScope();
										}}
										aria-label="Clear scope filter"
										_hover={{ bg: 'primary.bgAlphaHover' }}
									>
										<XOutline boxSize={rem(16)} color="primary.fg" />
									</Flex>
								)}
							</HStack>
							<DropdownMenu zIndex="tooltip" w={rem(280)}>
								<Box display="flex" flexDirection="column" h={rem(360)} maxH={rem(360)} overflow="hidden">
									{/* Toggle tabs for Teams/Departments - styled like filter chips */}
									{hasTeams && hasDepartments && (
										<Box px={rem(8)} py={rem(8)} borderBottom="1px solid" borderColor="border.soft" flexShrink={0}>
											<HStack spacing={rem(4)}>
												<Flex
													as="button"
													alignItems="center"
													justifyContent="center"
													h={rem(32)}
													px={rem(12)}
													borderRadius="full"
													border="1px solid"
													borderColor={scopeTab === 'teams' ? 'transparent' : 'border.default'}
													bg={scopeTab === 'teams' ? 'secondary.bg' : 'transparent'}
													color={scopeTab === 'teams' ? 'secondary.fgOnSurface' : 'text.default'}
													fontSize="sm"
													fontWeight="medium"
													cursor="pointer"
													flex={1}
													onClick={(e) => {
														e.stopPropagation();
														setScopeTab('teams');
													}}
													_hover={{ bg: scopeTab === 'teams' ? 'secondary.bgHover' : 'secondary.bgAlpha' }}
												>
													{t('leaderboard.filters.teams')}
												</Flex>
												<Flex
													as="button"
													alignItems="center"
													justifyContent="center"
													h={rem(32)}
													px={rem(12)}
													borderRadius="full"
													border="1px solid"
													borderColor={scopeTab === 'departments' ? 'transparent' : 'border.default'}
													bg={scopeTab === 'departments' ? 'secondary.bg' : 'transparent'}
													color={scopeTab === 'departments' ? 'secondary.fgOnSurface' : 'text.default'}
													fontSize="sm"
													fontWeight="medium"
													cursor="pointer"
													flex={1}
													onClick={(e) => {
														e.stopPropagation();
														setScopeTab('departments');
													}}
													_hover={{ bg: scopeTab === 'departments' ? 'secondary.bgHover' : 'secondary.bgAlpha' }}
												>
													{t('leaderboard.filters.departments')}
												</Flex>
											</HStack>
										</Box>
									)}

									{/* List of teams or departments based on tab - lightweight Buttons + search */}
									<ScopeOptionsList
										options={currentOptions}
										selectedValue={scopeTab === 'teams' ? state.selectedTeam : state.selectedDepartment}
										onSelect={handleScopeSelect}
										searchPlaceholder={scopeTab === 'teams' ? t('leaderboard.filters.searchTeams') : t('leaderboard.filters.searchDepartments')}
									/>

									{/* Clear button - always visible when filter is active */}
									{hasActiveScope && (
										<Box px={rem(8)} py={rem(8)} borderTop="1px solid" borderColor="border.soft" flexShrink={0}>
											<Button
												size="xs"
												variant="ghost"
												colorScheme="primary"
												onClick={(e) => {
													e.stopPropagation();
													handleClearScope();
												}}
												w="full"
											>
												{t('leaderboard.clear')}
											</Button>
										</Box>
									)}
								</Box>
							</DropdownMenu>
						</Dropdown>
					</Box>
				)}
			</HStack>

			{/* Show date range picker when custom is selected */}
			{state.selectedTimeframe === 'custom' && <DateRangePicker value={customDateRange} onChange={handleCustomDateRangeChange} isClearable={false} w="100%" />}
		</VStack>
	);
};
